import React, { useState } from 'react';
import { Save, Loader2, Shirt, PackageCheck } from 'lucide-react';
import {
  atlasApi, AtlasUser, Colaborador, EntregaUniforme, PecaUniforme,
  LABEL_PECA_UNIFORME,
} from '../../shared/atlasApi';
import ListaEditavel, { inputCls, labelCls } from '../../shared/ListaEditavel';
import FeedbackBanner, { Feedback } from '../../shared/FeedbackBanner';

interface Props {
  user: AtlasUser;
  colaborador: Colaborador;
  podeEditar: boolean;
  onSalvo: () => void;
}

const PECAS: PecaUniforme[] = ['camisa', 'calca', 'calcado', 'jaleco', 'epi'];

// Sugestões por peça — o campo continua livre (tamanhos variam por fornecedor)
const SUGESTOES: Record<PecaUniforme, string> = {
  camisa: 'PP, P, M, G, GG, XG',
  calca: '36, 38, 40, 42, 44...',
  calcado: '36 a 46',
  jaleco: 'PP, P, M, G, GG',
  epi: 'descrição livre',
};

const AbaUniforme: React.FC<Props> = ({ user, colaborador, podeEditar, onSalvo }) => {
  const e = colaborador.enriquecimento || {};
  const u0 = e.uniforme || {};
  const t0 = u0.tamanhos || {};

  const [tamanhos, setTamanhos] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    PECAS.forEach(p => { base[p] = t0[p] || ''; });
    // Migra o campo antigo `tamanho_camisa` quando o uniforme ainda não foi preenchido
    if (!base.camisa && e.tamanho_camisa) base.camisa = e.tamanho_camisa;
    return base;
  });
  const [entregas, setEntregas] = useState<EntregaUniforme[]>(
    Array.isArray(u0.entregas) ? u0.entregas.map(x => ({ ...x })) : [],
  );
  const [observacoes, setObservacoes] = useState(u0.observacoes || '');
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const salvar = async () => {
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.putUniforme(user, colaborador.funccodi, { tamanhos, entregas, observacoes });
      setFeedback({ tipo: 'ok', msg: 'Uniforme salvo com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const ro = !podeEditar;

  return (
    <div className="space-y-5">
      <FeedbackBanner feedback={feedback} />

      {/* Tamanhos */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <Shirt className="w-4 h-4" /> Tamanhos
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PECAS.map(p => (
            <div key={p}>
              <label className={labelCls}>{LABEL_PECA_UNIFORME[p]}</label>
              <input
                className={inputCls}
                disabled={ro}
                placeholder={SUGESTOES[p]}
                value={tamanhos[p] || ''}
                onChange={ev => setTamanhos(prev => ({ ...prev, [p]: ev.target.value }))}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Entregas */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <PackageCheck className="w-4 h-4" /> Entregas
        </div>
        <ListaEditavel<EntregaUniforme>
          itens={entregas}
          onChange={setEntregas}
          novoItem={() => ({ item: '', tamanho: '', quantidade: 1, data_entrega: '', devolvido: false, observacao: '' })}
          rotuloAdd="registrar entrega"
          vazioMsg="Nenhuma entrega registrada."
          podeEditar={podeEditar}
          renderLinha={(en, atualizar) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Item</label>
                <input className={inputCls} disabled={ro} placeholder="Camisa polo, bota de segurança..." value={en.item || ''} onChange={ev => atualizar({ item: ev.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Tamanho</label>
                <input className={inputCls} disabled={ro} value={en.tamanho || ''} onChange={ev => atualizar({ tamanho: ev.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Quantidade</label>
                <input type="number" min={1} className={inputCls} disabled={ro} value={en.quantidade ?? 1} onChange={ev => atualizar({ quantidade: Number(ev.target.value) })} />
              </div>
              <div>
                <label className={labelCls}>Data da entrega</label>
                <input type="date" className={inputCls} disabled={ro} value={(en.data_entrega || '').slice(0, 10)} onChange={ev => atualizar({ data_entrega: ev.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Observação</label>
                <input className={inputCls} disabled={ro} value={en.observacao || ''} onChange={ev => atualizar({ observacao: ev.target.value })} />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 md:col-span-2">
                <input type="checkbox" disabled={ro} checked={!!en.devolvido} onChange={ev => atualizar({ devolvido: ev.target.checked })} className="accent-purple-600" />
                Devolvido
              </label>
            </div>
          )}
        />
      </section>

      <div>
        <label className={labelCls}>Observações gerais</label>
        <textarea
          className={`${inputCls} min-h-[70px] resize-y`}
          disabled={ro}
          value={observacoes || ''}
          onChange={ev => setObservacoes(ev.target.value)}
        />
      </div>

      {podeEditar && (
        <div className="flex justify-end pt-1">
          <button
            onClick={salvar}
            disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
          >
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar uniforme
          </button>
        </div>
      )}
    </div>
  );
};

export default AbaUniforme;
